import gql from "graphql-tag";
import { useState, useRef, useEffect } from "react";
import { EASE } from "@/core/constants";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { useClientMount } from '@/providers/ClientMountProvider';

import { SoundExplorerFragment } from "@graphqlTypes";
import { ACFMediaFragment } from "@/fragments";

import { useDebug } from "@/utils/useDebugLog";
import { Container, CursorFollower, HeadlineAnim, RevealWrapper } from "@components";
import SoundExplorerSlide from "./SoundExplorerSlide";

import SkipIcon from "@/assets/svg/skip.svg";
import PauseIcon from "@/assets/svg/pause.svg";
import ArrowRightThin from '@/assets/svg/arrow-right-thin.svg';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView, motion } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';
import { useSwipeable } from 'react-swipeable';

import styles from "./SoundExplorer.module.scss";
import className from "classnames/bind";
const cx = className.bind(styles);

export default function SoundExplorer(props: SoundExplorerFragment) {
  const { eyebrow, title, copy, slides } = props;
  const { labels } = usePageWrapperData();

  const { log } = useDebug({
    name: "SoundExplorer",
    bgColor: "plum",
    enabled: false
  });
  log({ slides });

  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [showHitAreas, setShowHitAreas] = useState(false);

  const position = (index: number) => ({
    x: `calc(${index * -73}% - ${index * 3}rem)`,
    transition: { duration: 0.4 * animationSpeed, ease: EASE.InOutQuad },
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const prevCursorRoot = useRef<HTMLDivElement>(null);
  const nextCursorRoot = useRef<HTMLDivElement>(null);
  const firstAnimComplete = useRef(false);
  const hasTriggeredFirstAnim = useRef(false);
  const { mounted } = useClientMount();

  const handleNext = () => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % slides.length);
  };
  const handlePrev = () => {
    setActiveIndex(
      (prevIndex) => (prevIndex - 1 + slides.length) % slides.length
    );
  };

  const handleSlideFocus = (index: number) => {
    setActiveIndex(index);
  }

  const handleFirstAnimComplete = () => {
    if (!firstAnimComplete.current && hasTriggeredFirstAnim.current) {
      setAnimationSpeed(1);
      firstAnimComplete.current = true;
    }
  }

  const [titleHeights, setTitleHeights] = useState<Record<number, number>>({});
  const handleTitleHeightChange = (index: number, height: number) => {
    setTitleHeights(prev => ({ ...prev, [index]: height }));
  };

  // -------------------- Desktop description fade state

  const FADE_DURATION = 200;
  const REAPPEAR_DELAY = 140; // ms delay before animating back in
  const [displayedDescription, setDisplayedDescription] = useState(
    slides[0]?.description ?? ""
  );
  const [descVisible, setDescVisible] = useState(true);

  useEffect(() => {
    const newDesc = slides[activeIndex]?.description ?? "";

    // Run fade sequence on subsequent index changes
    setDescVisible(false);
    const t = window.setTimeout(() => {
      setDisplayedDescription(newDesc);
      setDescVisible(true);
    }, FADE_DURATION * animationSpeed);
    return () => window.clearTimeout(t);
  }, [activeIndex, slides]);

  // -------------------- Animation

  const rootEl = useRef<HTMLElement>(null);
  const { isDesktop, isReady } = useViewSize();
  const isRevealEnabled = true;
  const REVEAL = AnimVals;
  const triggerPercent = (mounted && isReady && isDesktop) ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  // -------------------- Set initial index & animation speed

  useEffect(() => {
    if (isDesktop && mounted) {
      setAnimationSpeed(2.5);
      if (slides.length >= 5) {
        setActiveIndex(3);
      } else if (slides.length >= 3) {
        setActiveIndex(2);
      } else {
        setActiveIndex(1);
      }
    }
  }, [isDesktop, mounted]);

  // -------------------- Change index for reveal animation & reset animation speed

  useEffect(() => {
    if (isInView && isDesktop && mounted) {
      setActiveIndex(activeIndex - 1);
      hasTriggeredFirstAnim.current = true;
    }
  }, [isDesktop, isInView, mounted]);

  useEffect(() => {
    setShowHitAreas(isDesktop);
  }, [isDesktop]);

  // -------------------- Swiping

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrev,
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: !isDesktop
  });

  // -------------------- Render

  return (
    <section className={cx(["sound-explorer"])} ref={rootEl}>
      <div {...swipeHandlers}>
        { mounted && isReady && isDesktop && showHitAreas && (
          <div
            className={cx("sound-explorer__left-hit")} ref={prevCursorRoot}
            onClick={handlePrev}
          >
            <CursorFollower
              circleText={labels?.ariaLabels?.prevButton}
              container={prevCursorRoot}
              icon={ArrowRightThin}
              iconClass={cx(["icon-prev"])}
              isVisible={isDesktop}
              mouseActive={true}
            />
          </div>
        )}
        <RevealWrapper isInView={isInView}>
          <div className={cx(["sound-explorer-content"])}>

            {/* Copy */}
            <Container className={cx(["copy-container"])}>
              <div className={cx(["copy"])}>
                <p className={cx(["eyebrow-l", "eyebrow"])}>{eyebrow}</p>
                <HeadlineAnim
                  tag="h2"
                  text={title}
                  className={cx(["title-uc-l", "headline"])}
                  isRevealEnabled={isRevealEnabled}
                  isInView={isInView}
                  maxCharsPerWord={10}
                />
                <p className={cx(["text-m", "body"])}>{copy}</p>
              </div>
            </Container>

            {/* Slides */}
            <div className={cx(["conveyor"])}>
              <div className={cx(["conveyor-track"])}>
                <motion.div
                  animate={position(activeIndex)}
                  onAnimationComplete={handleFirstAnimComplete}
                  className={cx(["conveyor-track-container"])}
                >
                  {slides.map((slide, index) => (
                    <SoundExplorerSlide
                      slide={slide}
                      index={index}
                      activeIndex={activeIndex}
                      onSlideFocus={handleSlideFocus}
                      count={slides.length}
                      key={index}
                      onTitleHeightChange={height => handleTitleHeightChange(index, height)}
                      animationSpeed={animationSpeed}
                    />
                  ))}
                </motion.div>

                <div className={cx(["controls-description-wrapper"])}>
                  <div className={cx(["controls"])}>
                    <button
                      className={cx([
                        "control",
                        "prev",
                        { disabled: activeIndex === 0 },
                      ])}
                      onClick={handlePrev}
                      aria-label="Previous"
                    >
                      {" "}
                      <SkipIcon />
                    </button>
                    {/* <button
                      className={cx(["control", "pause"])}
                      onClick={handlePause}
                      aria-label="Pause"
                    >
                      <PauseIcon /> Temp Removed since we don't have audio. -MA
                    </button> */}
                    <button
                      className={cx([
                        "control",
                        "next",
                        { disabled: activeIndex === slides.length - 1 },
                      ])}
                      onClick={handleNext}
                      aria-label="Next"
                    >
                      {" "}
                      <SkipIcon />
                    </button>
                  </div>

                  <div
                    className={cx(["description"])}
                    style={{ paddingTop: `${titleHeights[activeIndex] - 30}px` }}
                  >
                    <motion.p
                      className={cx(["text-m", "body"])}
                      animate={{
                        opacity: descVisible ? 1 : 0,
                        y: descVisible ? 0 : '100%',
                      }}
                      initial={false}
                      transition={{
                        duration: FADE_DURATION / 1000,
                        ease: descVisible ? EASE.OutQuad : EASE.InQuad,
                        delay: descVisible ? REAPPEAR_DELAY / 1000 : 0,
                      }}
                    >
                      {displayedDescription || ""}
                    </motion.p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </RevealWrapper>
        { mounted && isReady && isDesktop && showHitAreas && (
          <div
            className={cx("sound-explorer__right-hit")} ref={nextCursorRoot}
            onClick={handleNext}
          >
            <CursorFollower
              circleText={labels?.ariaLabels?.nextButton}
              icon={ArrowRightThin}
              iconClass={cx(["icon-next"])}
              container={nextCursorRoot}
              isVisible={isDesktop}
              mouseActive={true}
            />
          </div>
        )}
      </div>
    </section>
  );
}

SoundExplorer.fragments = {
  entry: gql`
    ${ACFMediaFragment}
    fragment SoundExplorer on InducteeDataImpactFlexibleContentSoundExplorerLayout {
      eyebrow
      title
      copy
      slides {
        albumColor
        albumImage {
          ...ACFMedia
        }
        albumName
        songTitle
        description
      }
    }
  `,
};
